import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  RefreshCw,
  Loader2,
  AlertTriangle,
  TrendingDown,
  Package,
  Clock,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { api } from "../lib/api";
import { computeSnapshotFreshness } from "../lib/freshness";
import { Button } from "./ui/button";

/**
 * Phase 101 (12/07/2026) — Widget « Stock machine Nayax ».
 *
 * Affiche l'état temps-réel d'une machine (via API Nayax `machineProducts`) :
 * - Table des slots avec stock actuel / capacité / manque / prix / vendout
 * - Bouton refresh manuel
 * - Détection des NayaxProductID sans mapping AM
 * - Adapté aux machines de 15 slots (petites) comme celles de 37 (Conthey)
 *
 * Props :
 *   distributorId — id AM du distributeur
 *   nayaxMachineId — l'id Nayax (si absent, message dédié)
 *   distributorName — pour toasts
 */
export function NayaxMachineWidget({ distributorId, nayaxMachineId, distributorName }) {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showUnmapped, setShowUnmapped] = useState(false);
  const [error, setError] = useState(null);

  const loadLatest = useCallback(async () => {
    if (!distributorId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/distributors/${distributorId}/latest-machine-snapshot`);
      setSnapshot(r.data);
    } catch (e) {
      if (e?.response?.status === 404) {
        // Pas encore de snapshot — pas une erreur, juste un état vide
        setSnapshot(null);
      } else {
        setError(e?.response?.data?.detail || "Erreur chargement snapshot");
      }
    } finally {
      setLoading(false);
    }
  }, [distributorId]);

  useEffect(() => {
    loadLatest();
  }, [loadLatest]);

  const doSync = async () => {
    if (!nayaxMachineId) {
      toast.error("Ce distributeur n'a pas de nayax_machine_id configuré");
      return;
    }
    setSyncing(true);
    try {
      const r = await api.post(`/distributors/${distributorId}/sync-machine-products`);
      toast.success(
        `✅ ${r.data.total_slots} slots synchronisés · ${r.data.mapped_slots} mappés${r.data.unmapped_nayax_ids?.length ? ` · ${r.data.unmapped_nayax_ids.length} non mappés` : ""}`
      );
      await loadLatest();
    } catch (e) {
      // Défense contre l'interception Cloudflare qui remplace le body JSON par
      // du HTML (« Bad gateway »). Si on reçoit du HTML, on affiche un message
      // générique plutôt que le texte cryptique extrait de la page d'erreur CDN.
      const raw = e?.response?.data;
      const detail = typeof raw === "object" ? raw?.detail : raw;
      const looksLikeHtml = typeof detail === "string" && detail.trim().startsWith("<");
      let msg;
      if (looksLikeHtml) {
        msg = "Erreur réseau ou proxy — la sync Nayax a échoué. Réessayez ou vérifiez votre token Nayax.";
      } else if (typeof detail === "string") {
        msg = detail;
      } else if (Array.isArray(detail)) {
        msg = detail.map((d) => d?.msg || JSON.stringify(d)).join(" · ");
      } else {
        msg = "Erreur de synchronisation Nayax";
      }
      toast.error(msg);
    } finally {
      setSyncing(false);
    }
  };

  if (!nayaxMachineId) {
    return (
      <div
        className="bg-[#FFF8E7] border border-[#E5D9B0] rounded-lg p-4 text-sm text-[#8C6B2F]"
        data-testid="nayax-widget-no-machine-id"
      >
        <AlertTriangle className="w-4 h-4 inline mr-2" />
        Ce distributeur n&apos;a pas de <code className="bg-white px-1 rounded">nayax_machine_id</code> configuré —
        la sync automatique avec Nayax est indisponible. Modifiez-le dans les infos distributeur.
      </div>
    );
  }

  return (
    <section
      className="bg-white border border-[#E5E5E0] rounded-lg overflow-hidden"
      data-testid="nayax-machine-widget"
    >
      <header className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#E5E5E0] bg-[#FAFAF7]">
        <div className="flex items-center gap-2 min-w-0">
          <Package className="w-4 h-4 text-[#1F4E3D] flex-shrink-0" />
          <h3 className="font-medium text-sm text-[#1A1C1A]">Stock machine · Nayax</h3>
          <span className="text-xs text-[#8C8C88] hidden sm:inline">
            · Machine <code className="bg-white px-1.5 py-0.5 rounded border border-[#E5E5E0]">{nayaxMachineId}</code>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => {
              // Phase 101bh — ouvre le PDF picklist basé sur snapshot MOMA
              const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
              // Fetch avec credentials pour envoyer le cookie de session, puis blob URL
              fetch(`${API}/api/distributors/${distributorId}/picklist-from-snapshot`, {
                method: "POST",
                credentials: "include",
              })
                .then(async (r) => {
                  if (!r.ok) {
                    const err = await r.json().catch(() => ({}));
                    throw new Error(err.detail || `HTTP ${r.status}`);
                  }
                  return r.blob();
                })
                .then((blob) => {
                  const url = URL.createObjectURL(blob);
                  window.open(url, "_blank");
                })
                .catch((e) => toast.error(e.message || "Erreur génération picklist"));
            }}
            disabled={!snapshot || syncing || loading}
            size="sm"
            variant="outline"
            className="border-[#8C6B2F] text-[#8C6B2F] hover:bg-[#FBF4E9] h-8 gap-1.5"
            data-testid="picklist-from-snapshot-btn"
            title="Génère un PDF « à charger » à partir du dernier snapshot MOMA (PAR − qty actuelle). Le livreur remplit lot + DLC à la main au dépôt."
          >
            🖨️ Picklist prep
          </Button>
          <Button
            onClick={doSync}
            disabled={syncing || loading}
            size="sm"
            className="bg-[#1F4E3D] hover:bg-[#173B2E] text-white h-8 gap-1.5"
            data-testid="nayax-sync-btn"
          >
            {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {syncing ? "Sync..." : "Rafraîchir depuis Nayax"}
          </Button>
        </div>
      </header>

      {error && (
        <div className="px-4 py-3 bg-[#FFEDEA] text-[#B93A25] text-sm border-b border-[#F0C8BF]" data-testid="nayax-widget-error">
          {error}
        </div>
      )}

      {loading && !snapshot && (
        <div className="px-4 py-8 text-center text-sm text-[#8C8C88]" data-testid="nayax-widget-loading">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Chargement du dernier snapshot...
        </div>
      )}

      {!loading && !snapshot && (
        <div className="px-4 py-8 text-center text-sm text-[#8C8C88]" data-testid="nayax-widget-empty">
          Aucun snapshot enregistré. Cliquez « Rafraîchir depuis Nayax » pour récupérer l&apos;état actuel de la machine.
        </div>
      )}

      {snapshot && (
        <>
          {/* KPIs résumés */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 px-4 py-3 border-b border-[#E5E5E0] bg-[#FDFDFB] text-xs">
            <Kpi label="Slots" value={snapshot.total_slots} testid="kpi-slots" />
            <Kpi label="Mappés AM" value={snapshot.mapped_slots} intent={snapshot.mapped_slots === snapshot.total_slots ? "ok" : "warn"} testid="kpi-mapped" />
            <Kpi label="Manque total" value={snapshot.total_missing} intent={snapshot.total_missing > 0 ? "warn" : "ok"} testid="kpi-missing" />
            <Kpi label="Vendout" value={snapshot.vendout_count} intent={snapshot.vendout_count > 0 ? "danger" : "ok"} testid="kpi-vendout" />
            <Kpi label="Slow movers" value={snapshot.slow_mover_count} intent={snapshot.slow_mover_count > 0 ? "warn" : "muted"} testid="kpi-slow" />
          </div>

          <div className="px-4 py-2 flex items-center justify-between text-[11px] text-[#8C8C88] border-b border-[#E5E5E0] bg-white">
            <span className="flex items-center gap-2 flex-wrap">
              <Clock className="w-3 h-3 inline" />
              <span>Sync {formatDate(snapshot.fetched_at)}</span>
              {snapshot.source === "scheduler" && <span>· auto</span>}
              <FreshnessBadge iso={snapshot.fetched_at} />
            </span>
          </div>

          {/* Table des slots */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FAFAF7] text-[10px] uppercase tracking-wider text-[#575A57]">
                <tr>
                  <th className="px-3 py-2 text-left w-12">Slot</th>
                  <th className="px-3 py-2 text-left">Produit</th>
                  <th className="px-3 py-2 text-right w-16">Stock</th>
                  <th className="px-3 py-2 text-right w-16">PAR</th>
                  <th className="px-3 py-2 text-right w-16">Manque</th>
                  <th className="px-3 py-2 text-right w-20 hidden md:table-cell">Prix</th>
                  <th className="px-3 py-2 text-right w-32 hidden lg:table-cell">Dernière vente</th>
                  <th className="px-3 py-2 text-center w-20">État</th>
                </tr>
              </thead>
              <tbody>
                {snapshot.slots
                  .slice()
                  .sort((a, b) => (parseInt(a.slot_code) || 0) - (parseInt(b.slot_code) || 0))
                  .map((s) => (
                    <tr key={s.machine_product_id} className="border-t border-[#F0F0EC] hover:bg-[#FDFDFB]">
                      <td className="px-3 py-2 font-mono text-xs text-[#575A57]">{s.slot_code || "-"}</td>
                      <td className="px-3 py-2">
                        {s.product_name_am ? (
                          <div className="flex flex-col gap-0.5">
                            <span className="text-[#1A1C1A]">{s.product_name_am}</span>
                            {s.desync && (
                              <span
                                className="text-[10px] text-[#B93A25] italic inline-flex items-center gap-1"
                                data-testid={`slot-desync-${s.slot_code}`}
                                title={`Nayax renvoie NayaxProductID ${s.nayax_product_id} qui pointe sur "${s.desync_nayax_name || '?'}" dans vos produits AM. Vérifiez le mapping Nayax MOMA ou vos doublons nayax_product_id.`}
                              >
                                <AlertTriangle className="w-3 h-3" />
                                Nayax désync ({s.desync_nayax_name || "?"})
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-[#B93A25] italic">
                            ⚠ Nayax ID {s.nayax_product_id} non mappé
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">
                        <span className={s.stock_current === 0 ? "text-[#B93A25] font-bold" : "text-[#1A1C1A]"}>
                          {s.stock_current}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-[#8C8C88]">{s.capacity_max}</td>
                      <td className="px-3 py-2 text-right font-mono">
                        {s.missing_qty > 0 ? (
                          <span className="text-[#8C6B2F] font-medium">{s.missing_qty}</span>
                        ) : (
                          <span className="text-[#A3A6A3]">0</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs text-[#575A57] hidden md:table-cell">
                        {s.machine_price != null ? `${s.machine_price} CHF` : "-"}
                      </td>
                      <td className="px-3 py-2 text-right text-xs text-[#8C8C88] hidden lg:table-cell">
                        {s.last_sale_dt ? formatDate(s.last_sale_dt) : "—"}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {s.vendout_bit && (
                          <span className="inline-block px-1.5 py-0.5 text-[10px] rounded bg-[#B93A25] text-white uppercase" title="Slot déclaré vide par la machine">
                            VendOut
                          </span>
                        )}
                        {s.slow_mover && !s.vendout_bit && (
                          <span className="inline-block px-1.5 py-0.5 text-[10px] rounded bg-[#F0D08B] text-[#8C6B2F] uppercase" title="Rotation lente selon Nayax">
                            <TrendingDown className="w-3 h-3 inline" />
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {/* NayaxProductID non mappés */}
          {snapshot.unmapped_nayax_ids?.length > 0 && (
            <div className="border-t border-[#F0C8BF] bg-[#FFF8F5] px-4 py-3">
              <button
                type="button"
                onClick={() => setShowUnmapped((v) => !v)}
                className="flex items-center gap-2 text-sm text-[#B93A25] hover:underline w-full"
                data-testid="unmapped-toggle"
              >
                <AlertTriangle className="w-4 h-4" />
                {snapshot.unmapped_nayax_ids.length} produit(s) Nayax sans correspondance AM
                {showUnmapped ? <ChevronUp className="w-4 h-4 ml-auto" /> : <ChevronDown className="w-4 h-4 ml-auto" />}
              </button>
              {showUnmapped && (
                <div className="mt-2 text-xs text-[#575A57] leading-relaxed">
                  <p className="mb-2">
                    Ces NayaxProductID existent dans la machine mais ne correspondent à aucun produit AM (via <code className="bg-white px-1 rounded border">nayax_product_id</code>).
                    Ajoutez-les dans <a href="/produits" className="underline text-[#1F4E3D]">Produits</a> pour compléter le mapping.
                  </p>
                  <ul className="grid grid-cols-2 sm:grid-cols-4 gap-1 font-mono">
                    {snapshot.unmapped_nayax_ids.map((id) => (
                      <li key={id} className="bg-white px-2 py-1 rounded border border-[#F0C8BF]" data-testid={`unmapped-${id}`}>
                        {id}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

function Kpi({ label, value, intent = "muted", testid }) {
  const colors = {
    ok: { bg: "bg-[#E8F2EC]", text: "text-[#1F4E3D]" },
    warn: { bg: "bg-[#FBF4E9]", text: "text-[#8C6B2F]" },
    danger: { bg: "bg-[#FFEDEA]", text: "text-[#B93A25]" },
    muted: { bg: "bg-[#F4F4F0]", text: "text-[#575A57]" },
  }[intent];
  return (
    <div className={`${colors.bg} rounded px-2 py-1.5`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-[#8C8C88]">{label}</div>
      <div className={`font-mono text-base ${colors.text}`}>{value}</div>
    </div>
  );
}

/**
 * Phase 101bs (16/02/2026) — Badge visuel de fraîcheur du snapshot MOMA.
 *
 * Auto-refresh toutes les 30 secondes pour maintenir le compte à jour.
 * La logique pure de calcul est dans `lib/freshness.js` (testée séparément).
 */
function FreshnessBadge({ iso }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!iso) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, [iso]);

  const freshness = computeSnapshotFreshness(iso, now);
  if (!freshness) return null;

  const cls =
    freshness.tone === "fresh"
      ? "bg-[#E8F2EC] text-[#1F4E3D] border-[#C5DBCF]"
      : freshness.tone === "stale"
        ? "bg-[#FFEDEA] text-[#B93A25] border-[#F0C8BF]"
        : "bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D3A8]";

  const tooltip =
    freshness.tone === "stale"
      ? "Snapshot obsolète — rafraîchir avant d'agir"
      : freshness.tone === "fresh"
        ? "Snapshot récent, données fiables"
        : "Snapshot en cours de péremption";

  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium ${cls}`}
      title={tooltip}
      data-testid="snapshot-freshness-badge"
      data-tone={freshness.tone}
    >
      {freshness.label}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso.slice(0, 16).replace("T", " ");
    return d.toLocaleString("fr-CH", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default NayaxMachineWidget;
